import { useTranslation } from "react-i18next";
import RwandaCountry from '@/assets/images/pillar-business.jpg';
import KenyaCountry from '@/assets/images/pillar-kenya.jpg';
import TanzaniaCountry from '@/assets/images/pillar-tanzania.jpg';
import UgandaCountry from '@/assets/images/pillar-uganda.jpg';
import EthiopieCountry from '@/assets/images/pillar-ethiopia.jpg';
import BurundiCountry from '@/assets/images/pillar-burundi.jpg';
import CongoCountry from '@/assets/images/pillar-congo.jpg';




const getPortals = (t: (key: string) => string) => [
  {
    country: "Rwanda", flag: "🇷🇼", authority: "Rwanda Development Board (RDB)",
    tagline: "Africa's #2 easiest country to do business",
    description: "The RDB is your single gateway to set up a business, get investment approvals, and access government incentives in Rwanda. 100% foreign ownership and digital-first processes.",
    link: "https://www.rdb.rw", linkLabel: "Visit RDB Portal",
    highlights: ["100% foreign ownership", "No minimum capital", "3-day incorporation", "Special Economic Zones"],
    image: RwandaCountry,
    accent: "#00A651",
  },
  {
    country: "Kenya", flag: "🇰🇪", authority: "Kenya Investment Authority (KenInvest)",
    tagline: "East Africa's largest economy & tech hub",
    description: "KenInvest facilitates both local and foreign investment in Kenya, providing investment certificates, investor aftercare, and linkages to incentives across all sectors.",
    link: "https://www.keninvest.go.ke", linkLabel: "Visit KenInvest Portal",
    highlights: ["Silicon Savannah tech hub", "Port of Mombasa access", "Nairobi financial centre", "Special Economic Zones"],
    image: KenyaCountry,
    accent: "#006600",
  },
  {
    country: "Tanzania", flag: "🇹🇿", authority: "Tanzania Investment Centre (TIC)",
    tagline: "Strategic gateway to Southern African markets",
    description: "The TIC provides one-stop facilitation for investment, granting Certificates of Incentives and helping investors navigate regulatory requirements across all key sectors.",
    link: "https://www.tic.go.tz", linkLabel: "Visit TIC Portal",
    highlights: ["SADC market access", "Tourism & natural resources", "Port of Dar es Salaam", "Agricultural potential"],
    image: TanzaniaCountry,
    accent: "#1D9F5F",
  },
  {
    country: "Uganda", flag: "🇺🇬", authority: "Uganda Investment Authority (UIA)",
    tagline: "Gateway to South Sudan, DRC & Central Africa",
    description: "The UIA promotes Uganda as an investment destination, facilitating business registration, licensing, and connecting investors with opportunities in energy, agriculture, and manufacturing.",
    link: "https://www.ugandainvest.go.ug", linkLabel: "Visit UIA Portal",
    highlights: ["Oil & gas sector", "Fertile agricultural land", "Hydropower resources", "Regional corridor hub"],
    image: UgandaCountry,
    accent: "#0A1628",
  },
  {
    country: "Ethiopia", flag: "🇪🇹", authority: "Ethiopian Investment Commission (EIC)",
    tagline: "Africa's fastest-growing economy",
    description: "The EIC attracts and facilitates investment into Ethiopia's rapidly expanding economy — home to Africa's largest population and a booming manufacturing and services sector.",
    link: "https://www.investethiopia.gov.et", linkLabel: "Visit EIC Portal",
    highlights: ["115M+ population market", "Industrial parks", "Low labour costs", "Addis Ababa air hub"],
    image: EthiopieCountry,
    accent: "#078930",
  },
  {
    country: "Burundi", flag: "🇧🇮", authority: "API Burundi (Investment Promotion Agency)",
    tagline: "Emerging market at the heart of East Africa",
    description: "The API promotes and facilitates investment in Burundi, offering guidance on regulatory procedures and business opportunities in agriculture, mining, and tourism sectors.",
    link: "https://www.api.bi", linkLabel: "Visit API Burundi Portal",
    highlights: ["Lake Tanganyika access", "Mining & minerals", "EAC membership", "Agricultural exports"],
    image: BurundiCountry,
    accent: "#CE1126",
  },
  {
    country: "DRC", flag: "🇨🇩", authority: "National Investment Promotion Agency (ANAPI)",
    tagline: "Vast resources & Africa's third-largest market",
    description: "ANAPI promotes and facilitates both local and foreign investment into the DRC's vast natural resources, agricultural potential, and growing consumer market of 100M+ people.",
    link: "https://www.anapi.co", linkLabel: "Visit ANAPI Portal",
    highlights: ["100M+ consumer market", "Mining & energy hub", "Congo Basin agriculture", "Regional trade gateway"],
    image: CongoCountry,
    accent: "#007FFF",
  },
];

export default function InvestmentPortals() {
  const { t } = useTranslation();
  const portals = getPortals(t);

  return (
    <section className="py-24 bg-white">
      <div className="w-full px-6 lg:px-12">
        <div className="mb-14">
          <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-3">{t("portalTag")}</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight">
                {t("portalTitle1")} <span className="text-[#00C2C2]">{t("portalTitle2")}</span>
              </h2>
              <p className="text-gray-500 text-base mt-4 max-w-2xl leading-relaxed">
                {t("portalDesc")}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0A1628]/10 text-[#00C2C2]">
                <i className="ri-global-line" />
              </div>
              <span><strong className="text-[#0A1628]">{t("portalCount")}</strong></span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portals.map((p) => (
            <div key={p.country} className="group rounded-xl border border-gray-100 overflow-hidden hover:border-[#00C2C2]/40 hover:-translate-y-1 transition-all duration-300 flex flex-col bg-white">
              <div className="relative w-full h-44 overflow-hidden">
                <img src={p.image} alt={p.country} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-[#0A1628]/10 to-transparent" />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <span className="text-2xl">{p.flag}</span>
                  <span className="text-white font-bold text-base">{p.country}</span>
                </div>
              </div>

              <div className="p-5 flex flex-col flex-1">
                <div className="mb-3">
                  <p className="text-xs font-bold text-[#00C2C2] uppercase tracking-wide mb-1">{p.authority}</p>
                  <p className="text-xs text-gray-400 italic">{p.tagline}</p>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">{p.description}</p>

                <div className="grid grid-cols-2 gap-1.5 mb-5">
                  {p.highlights.map((h) => (
                    <div key={h} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-check-line text-[#00C2C2] shrink-0" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>

                <a href={p.link} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center justify-center gap-2 bg-[#0A1628] text-white text-xs font-bold px-4 py-2.5 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-external-link-line" />
                  {p.linkLabel}
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 bg-[#f8f9fc] rounded-xl p-8 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-[#0A1628] font-bold text-lg mb-1">{t("portalNeedGuidance")}</p>
            <p className="text-gray-500 text-sm">{t("portalGuidanceDesc")}</p>
          </div>
          <a href="#registration" className="bg-[#0A1628] text-white text-sm font-bold px-7 py-3.5 rounded-full hover:bg-[#0A1628] transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 shrink-0">
            <i className="ri-customer-service-2-line" />
            {t("portalTalkTeam")}
          </a>
        </div>
      </div>
    </section>
  );
}
